import React from 'react';
import { Alert, Button } from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import useOrderCheckRetry from '../../hooks/useOrderCheckRetry.hooks';
import { selectIsAgentActionBlocked } from '../../redux/features/agentMode/agentMode.selector';

/**
 * Warning banner with a Retry action, shown when the latest order has gone
 * unconfirmed — no check event within the configured timeout (see
 * useOrderCheckRetry). Clicking Retry resends the current order so the agent
 * no longer has to add/remove a throwaway item to force one.
 *
 * With auto-retry armed (HITL-1515) the banner also shows a countdown and the
 * resend fires by itself when it ends; the button stays available to resend
 * sooner.
 */
export default function OrderCheckRetryAlert({
  isUnifiedMenu,
  menuUnificationOrderFailureCallback,
  menuUnificationOrderSuccessCallback,
}: {
  isUnifiedMenu: boolean;
  menuUnificationOrderFailureCallback: Function;
  menuUnificationOrderSuccessCallback: Function;
}) {
  const {
    isCheckOverdue,
    canRetry,
    isRetrying,
    autoRetrySecondsRemaining,
    retry,
  } = useOrderCheckRetry({
    isUnifiedMenu,
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
  });

  // HITL-1606: outside Intercept the agent may look but not act. UX only —
  // useAgentInterception refuses the emit regardless.
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);

  // This alert IS the retry offer — with no retry possible (a stuck
  // HITL-clicked final: its cart is already cleared, so a resend would send
  // an empty final) it renders nothing, exactly as before HITL-1527. The
  // overdue clock alone changes nothing here.
  if (!isCheckOverdue || !canRetry) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      // Pinned to the top of the cart's scroll container so the alert stays
      // visible and actionable on long carts — items scroll underneath
      // (HITL-1483). Works in both layouts: OrderReview is a direct child of
      // the scrolling element (legacy #cartContainer / hitl3 AgentCartCard
      // body), so sticky anchors to that scrollport.
      sx={{ position: 'sticky', top: 0, zIndex: 10, marginBottom: '8px' }}
      action={
        // Disabled while a resend is in flight — a second click would send a
        // duplicate order whose check would supersede (and discard) the
        // first's. Re-enabled when the resend lands as a new latest request;
        // on the legacy (non-menu-module) path that happens synchronously, so
        // the disable is only observable on the menu-module HTTP round trip.
        <Button
          color="inherit"
          size="small"
          onClick={retry}
          disabled={isRetrying || isAgentActionBlocked}
        >
          Retry
        </Button>
      }
    >
      {autoRetrySecondsRemaining != null
        ? `The POS hasn't confirmed the order — resending in ${autoRetrySecondsRemaining}s, or click Retry to resend now`
        : "The POS hasn't confirmed the order — click Retry to resend it"}
    </Alert>
  );
}
